import VueRouter, { RouteConfig } from "vue-router";
import StyleguideRoutes from "router/Styleguide";
import Authentication from "router/Authentication";
import RegistrationRoutes from "router/Registration";
import AdminRoutes from "router/Admin";
import SurveysRoutes from "router/Doctor/Surveys";
import StatsRoutes from "router/Doctor/Stats";
import QrFlowRoutes from "router/QrFlow";
import PatientRoutes from "router/Patient";

import { User } from "models";
import { authStore } from "store/typed";
import { loadTenantConfig } from "utils/tenantConfig";
import { openToAnyone, rolesAllow } from "router/access";

const routes: RouteConfig[] = [
  {
    path: "/",
    component: () => import(/* webpackChunkName: 'commons' */ "views/Home.vue"),
    name: "root",
    meta: { role: ["anyone"] },
  },
  {
    path: "/admin",
    component: () =>
      import(/* webpackChunkName: 'commons' */ "views/Container.vue"),
    meta: { role: ["admin", "superadmin", "local_admin"] },
    children: [
      {
        path: "/",
        redirect: "databases",
        name: "secure_root",
        meta: { role: ["superadmin", "admin", "local_admin"] },
      },
      ...AdminRoutes,
    ],
  },
  {
    path: "/doctor",
    component: () =>
      import(/* webpackChunkName: 'commons' */ "views/Container.vue"),
    meta: { role: ["doctor"] },
    children: [
      {
        path: "",
        name: "doctor_dashboard",
        component: () =>
          import(/* webpackChunkName: 'doctor' */ "views/Doctor/Dashboard.vue"),
        meta: { role: ["doctor"] },
      },
      // Stats first: "databases/:databaseId/:role" of the survey list
      // would otherwise capture ".../stats" when the URL is opened directly.
      ...StatsRoutes,
      ...SurveysRoutes,
    ],
  },
  ...RegistrationRoutes,
  ...QrFlowRoutes,
  ...PatientRoutes,
  ...StyleguideRoutes,
  ...Authentication,

  {
    path: "/accesso-sospeso",
    component: () =>
      import(/* webpackChunkName: 'commons' */ "views/Suspended.vue"),
    name: "suspended",
    meta: { role: ["anyone"] },
  },
  {
    path: "/404",
    component: () =>
      import(/* webpackChunkName: 'commons' */ "views/NotFound.vue"),
    name: "404",
    meta: { role: ["anyone"] },
  },
  {
    path: "*",
    component: () =>
      import(/* webpackChunkName: 'commons' */ "views/NotFound.vue"),
    name: "fallback_404",
    meta: { role: ["anyone"] },
  },
];

const router = new VueRouter({
  base: "/",
  routes,
  mode: "history",
});

router.beforeEach(async (to, _from, next) => {
  // A suspended tenant shows only the suspended page; the API refuses
  // everything else anyway (423).
  const config = await loadTenantConfig();
  const suspended = !!config.account?.suspended;
  if (suspended !== (to.name === "suspended")) {
    next(suspended ? { name: "suspended" } : "/");
    return;
  }
  if (suspended) {
    next();
    return;
  }

  const matched = to?.matched || [];

  try {
    const user = await authStore.authenticate();
    if (matched.some((record) => record.meta.noAuth)) {
      next("/");
      return;
    }

    // The roles of the route actually opened: a child can narrow its
    // parent's (see router/access).
    if (rolesAllow(matched, user?.role)) {
      next();
      return;
    } else {
      next(false);
      return;
    }
  } catch (e) {
    if (openToAnyone(matched)) {
      next();
      return;
    } else {
      next(`/login?redirect=${encodeURIComponent(window.location.toString())}`);
    }
  }
});

export default router;
